import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useClients } from '~/api/clients';
import { Text } from '~/components';
import { Field, Select } from '~/components/Form';

type FormWithCondition = {
  condition: { id: number; type: 'condition' | 'customCondition' | 'customAgreement' } | null;
  contactId: number | null;
  specialConditions: string;
};

type Props<T extends FormWithCondition> = {
  form: T extends FormWithCondition ? UseFormReturn<T> : never;
  required?: boolean;
};

export const FormCustomAgreement = <T extends FormWithCondition>({ form: { control, setValue, watch }, required = true }: Props<T>) => {
  const { data: clients } = useClients();

  const watchContactId = watch('contactId');
  const selectedContact = clients.flatMap(({ contacts }) => contacts).find((contact) => contact.id === watchContactId) ?? null;

  const customAgreements = useMemo(() => selectedContact?.customAgreements ?? [], [selectedContact?.customAgreements]);
  const customAgreementOptions = useMemo(
    () =>
      customAgreements.map((customAgreement) => ({
        value: { id: customAgreement.id, type: 'customAgreement' as const },
        label: customAgreement.description,
      })),
    [customAgreements],
  );

  const { t } = useTranslation(['invoices', 'validation']);

  if (!selectedContact || customAgreements.length === 0) return null;

  return (
    <>
      <Text>{t('invoices:fields.customAgreementId.description', { count: customAgreements.length, name: selectedContact.fullName })}</Text>

      <Controller
        control={control}
        name="condition"
        render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('invoices:fields.customAgreementId.label')}</Field.Label>

            <Select.Root
              itemToStringLabel={(item) => customAgreementOptions.find(({ value }) => item.id === value.id)?.label ?? ''}
              onValueChange={(newValue) => {
                onChange(newValue);

                const customAgreement = customAgreements.find(({ id }) => id === newValue?.id) ?? null;

                setValue(
                  'specialConditions',
                  customAgreement !== null
                    ? t('invoices:fields.specialConditions.custom', {
                        date: dayjs(customAgreement.date).format('DD-MM-YYYY'),
                        lng: selectedContact.language,
                      })
                    : '',
                );
              }}
              value={value}
            >
              <Select.Button placeholder={t('invoices:fields.customAgreementId.placeholder')} ref={ref} />
              <Select.List>
                {customAgreementOptions.map((option) => (
                  <Select.Item key={option.value.id} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: required, message: t('validation:required') } }}
      />
    </>
  );
};
